import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Accounting Management) ---
import {
    // Core Concepts & General
    faCalculator,       // Accounting / Calculations
    faMoneyCheckAlt,    // Financial Transactions / Integrity
    faFileInvoiceDollar,// Daily Records / Statements / Expenses
    faTasks,            // Operations / Efficiency
    faCheckCircle,      // Compliance / Accuracy
    // Critical Role (Benefits)
    faBalanceScale,     // Financial Integrity / Compliance
    faExchangeAlt,      // Cash Flow Management (In/Out)
    // faCheckCircle reused for Compliance
    faLightbulb,        // Strategic Decision-Making
    // Key Components
    faUniversity,       // Bank Reconciliation
    faChartPie,         // Financial Statements
    faReceipt,          // Expense Tracking / Budget Management
    faArrowsAltH,       // Accounts Payable/Receivable (Flow)
    faUsersCog,         // Payroll / Staff Benefits Management
    // Tools & Resources
    faFileExcel,        // Customizable Templates (Excel)
    faTachometerAlt,    // Financial Dashboards
    faCogs,             // Automated Software / Systems
    // Tax Compliance
    faLandmark,         // Tax / Government / Regulatory Reporting
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faProjectDiagram, faMapSigns, faBullseye, faSitemap, faLaptopCode, faGavel,
    faHandHoldingDollar, faExpandArrowsAlt, faTrophy, faThumbsUp, faBuilding, faMedal,
    faSearchLocation, faBoxOpen, faComments, faTags, faStar, faPrint, faMousePointer,
    faNewspaper, faCalendarCheck, faCrosshairs, faUserTie, faStream, faSearchDollar,
    faSmileBeam, faRoute, faConciergeBell
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Critical Role
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Key Components
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Tools & Resources
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Tax Compliance

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for accounting/finance
    const heroBgImage = 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Calculator/financial documents image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Day-to-Day Accounting Management for Hospitals</h1>
            <p className="cliniqor-sub-heading">
              Ensuring Financial Health and Operational Success with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Effectively managing high-volume daily financial transactions is vital. Cliniqor provides comprehensive solutions for accurate, timely accounting, empowering hospitals to track, analyze, and optimize performance.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- CRITICAL ROLE OF DAILY ACCOUNTING --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-features-section cliniqor-section-bg-gradient"> {/* Use features layout */}
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>The Critical Role of Day-to-Day Accounting in Hospitals</h2>
              <p>Organized tracking for smooth operations, compliance, and audit readiness.</p>
            </div>
             {/* Using Features Grid for the 4 critical role points */}
            <div className="cliniqor-features-grid">
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMoneyCheckAlt} /></div>
                        <h4>1. Financial Integrity & Transparency</h4>
                        <p>Accurate recording fosters internal control, supports audits, and builds stakeholder trust.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faExchangeAlt} /></div>
                        <h4>2. Cash Flow Management</h4>
                        <p>Monitoring inflows/outflows prevents shortfalls, ensuring liquidity for daily operational needs.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBalanceScale} /></div>
                        <h4>3. Regulatory Compliance</h4>
                        <p>Adhering to tax laws, reporting rules, and healthcare standards to avoid penalties.</p>
                    </div>
                </div>
                <div className="cliniqor-feature-card cliniqor-fade-in-up">
                    <div className="cliniqor-feature-card-content">
                        <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLightbulb} /></div>
                        <h4>4. Strategic Decision-Making</h4>
                        <p>Providing timely financial data for budgeting, investments, and resource allocation.</p>
                    </div>
                </div>
            </div>
          </div>
        </section>

        {/* --- KEY COMPONENTS OF DAILY ACCOUNTING (Detailed Planning Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Key Components of Day-to-Day Accounting Management</h2>
               <p>Essential practices for maintaining financial control and operational smoothness.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 5 Components */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUniversity} /></div>
                    <div className="cliniqor-content"><h4>1. Bank Reconciliation & Cash Flow Management</h4> <p>Matching internal records with bank statements, identifying discrepancies, managing liquidity.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChartPie} /></div>
                    <div className="cliniqor-content"><h4>2. Preparation of Financial Statements</h4> <p>Regularly creating income statements, balance sheets, cash flow statements for performance tracking.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faReceipt} /></div>
                    <div className="cliniqor-content"><h4>3. Expense Tracking & Budget Management</h4> <p>Monitoring daily costs (salaries, supplies, utilities), categorizing expenses, ensuring budgetary control.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faArrowsAltH} /></div>
                    <div className="cliniqor-content"><h4>4. Accounts Payable & Receivable Management</h4> <p>Tracking payments owed to suppliers (AP) and owed by patients/insurers (AR) for healthy cash flow.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsersCog} /></div>
                    <div className="cliniqor-content"><h4>5. Payroll & Staff Benefits Management</h4> <p>Accurate calculation of wages/deductions, managing benefits administration (insurance, retirement).</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- TOOLS & RESOURCES (Features Section - Reused) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Tools and Resources for Effective Day-to-Day Accounting</h2>
                 <p>Cliniqor's suite designed to streamline processes and enhance management.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Tools --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faFileExcel} /></div>
                            <h4>1. Customizable Accounting Templates</h4>
                            <p>Easy-to-use Excel templates for organizing income, expenses, budgets, reporting.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTachometerAlt} /></div>
                            <h4>2. Financial Dashboards</h4>
                            <p>Real-time overview of key metrics (cash flow, revenue, expenses) for quick insights.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCogs} /></div>
                            <h4>3. Automated Financial Software</h4>
                            <p>Integrating systems to automate billing, invoicing, reporting, reducing errors.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

       {/* --- TAX COMPLIANCE (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <FontAwesomeIcon icon={faLandmark} size="2x" style={{ marginRight: '15px', color: '#007bff' }} /> {/* Optional icon */}
                 <h2>Tax Compliance and Financial Reporting</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Beyond daily management, Cliniqor provides guidance on tax regulation compliance and healthcare-specific reporting. Our services ensure accurate records for income/expenses, timely tax filing, and meeting regulatory obligations.</p>
                   <p>Staying compliant avoids penalties and legal issues. Accurate data also facilitates smooth audits and financial reviews.</p>
              </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Streamline Your Hospital's Financial Operations</h2>
          <p>Effective day-to-day accounting is essential. Cliniqor offers the tools, expertise, and support to manage finances accurately, ensure compliance, and maintain financial health, allowing you to focus on quality patient care.</p>
          <a href="#contact" className="cliniqor-cta-button">Request Accounting Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;